import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getMySubscription } from '../lib/api';

const SubscriptionContext = createContext(null);

// Feature to tier mapping (mirrors backend)
const FEATURE_TIER_MAP = {
  // Location tier (independent portal)
  location_portal: ['location', 'pro', 'enterprise'],
  employee_management: ['location', 'pro', 'enterprise'],
  
  // Basic tier features
  dashboard: ['basic', 'pro', 'enterprise'],
  locations: ['basic', 'pro', 'enterprise'],
  collections: ['location', 'basic', 'pro', 'enterprise'],
  money_in_out: ['location', 'basic', 'pro', 'enterprise'],
  basic_reports: ['basic', 'pro', 'enterprise'],
  notifications: ['location', 'basic', 'pro', 'enterprise'],
  search_sort: ['basic', 'pro', 'enterprise'],
  inventory: ['location', 'basic', 'pro', 'enterprise'],
  analytics: ['basic', 'pro', 'enterprise'],
  subscription: ['location', 'basic', 'pro', 'enterprise'],
  
  // Pro tier features
  activity_logs: ['pro', 'enterprise'],
  location_portal: ['pro', 'enterprise'],
  employee_management: ['pro', 'enterprise'],
  expenses: ['pro', 'enterprise'],
  issues: ['pro', 'enterprise'],
  location_managers: ['pro', 'enterprise'],
  users: ['pro', 'enterprise'],
  advanced_reports: ['pro', 'enterprise'],
  machine_accounting: ['pro', 'enterprise'],
  partner_payouts: ['pro', 'enterprise'],
  role_permissions: ['pro', 'enterprise'],
  partners: ['pro', 'enterprise'],
  vendors: ['pro', 'enterprise'],
  referers: ['pro', 'enterprise'],
  
  // Enterprise tier features
  routes: ['enterprise'],
  invoices: ['enterprise'],
  companies: ['enterprise'],
  multi_company: ['enterprise'],
};

// Get required tier for a feature
const getRequiredTier = (feature) => {
  const tiers = FEATURE_TIER_MAP[feature];
  if (!tiers || tiers.length === 0) return 'enterprise';
  return tiers[0]; // First tier in list is minimum required
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasSubscription, setHasSubscription] = useState(false);

  // Feature access map from server
  const [canAccessFeature, setCanAccessFeature] = useState({});

  const fetchSubscription = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await getMySubscription();
      const data = response.data;
      
      setSubscription(data.subscription);
      setHasSubscription(data.has_subscription);
      setCanAccessFeature(data.can_access_feature || {});
      setError(null);
    } catch (err) {
      setError(err.message);
      setSubscription(null);
      setHasSubscription(false);
      setCanAccessFeature({});
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  // eslint-disable-next-line react-hooks/exhaustive-deps


  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Check if user can access a specific feature
  const canAccess = useCallback((feature) => {
    // Super admins and demo admins always have access
    if (user?.role === 'super_admin' || user?.role === 'demo_admin') return true;
    
    // All non-super roles: access is controlled by role permissions, not subscription
    // Subscription tier is used for upgrade prompts and locked-feature indicators only
    if (user?.role === 'admin' || user?.role === 'manager' || user?.role === 'location_manager' || user?.role === 'employee' || user?.role === 'location_user') return true;
    
    // During loading, allow access to prevent flickering
    if (loading) return true;
    
    // If subscription exists, use it for feature gating
    if (hasSubscription) {
      return canAccessFeature[feature] ?? false;
    }
    
    // Default: allow access (role permissions are the primary gate)
    return true;
  }, [user, loading, hasSubscription, canAccessFeature]);

  // Get current tier
  const getCurrentTier = useCallback(() => {
    return subscription?.tier || null;
  }, [subscription]);

  // Check if subscription is active
  const isActive = useCallback(() => {
    if (!subscription) return false;
    return ['active', 'trialing', 'admin_override'].includes(subscription.status);
  }, [subscription]);

  // Check if in trial
  const isTrialing = useCallback(() => {
    return subscription?.status === 'trialing';
  }, [subscription]);

  // Get days remaining
  const getDaysRemaining = useCallback(() => {
    if (!subscription) return 0;
    const end = new Date(subscription.current_period_end);
    const now = new Date();
    return Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
  }, [subscription]);

  // Get location limit
  const getLocationLimit = useCallback(() => {
    if (!subscription) return 0;
    return subscription.included_locations + (subscription.extra_locations || 0);
  }, [subscription]);

  // Get upgrade info for a feature
  const getUpgradeInfo = useCallback((feature) => {
    const requiredTier = getRequiredTier(feature);
    const currentTier = subscription?.tier || 'none';
    return {
      requiredTier,
      currentTier,
      needsUpgrade: !canAccess(feature),
    };
  }, [subscription, canAccess]);

  const value = {
    subscription,
    loading,
    error,
    hasSubscription,
    canAccess,
    canAccessFeature,
    getCurrentTier,
    isActive,
    isTrialing,
    getDaysRemaining,
    getLocationLimit,
    getUpgradeInfo,
    getRequiredTier,
    refreshSubscription: fetchSubscription,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
